
  const STORAGE_KEY = 'compare_products';
  const MAX_COMPARE_ITEMS = 4;

  /**
   * @returns {string[]}
   */
  function getCompareList() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => String(item).trim())
        .filter(Boolean)
        .filter((value, index, list) => list.indexOf(value) === index);
    } catch (error) {
      console.warn('[compare] Failed to parse compare list from localStorage.', error);
      return [];
    }
  }

  /**
   * @param {string[]} list
   */
  function setCompareList(list) {
    const normalized = Array.isArray(list)
      ? list
          .map((item) => String(item).trim())
          .filter(Boolean)
          .filter((value, index, arr) => arr.indexOf(value) === index)
          .slice(0, MAX_COMPARE_ITEMS)
      : [];

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    window.dispatchEvent(
      new CustomEvent('compare:updated', {
        detail: {
          list: normalized,
          count: normalized.length,
          max: MAX_COMPARE_ITEMS,
        },
      })
    );

    updateCompareButtons(normalized);
    return normalized;
  }

  /**
   * @param {string} productHandle
   */
  function addToCompare(productHandle) {
    const handle = String(productHandle).trim();
    if (!handle) return { ok: false, reason: 'invalid_product_handle' };

    const list = getCompareList();
    if (list.includes(handle)) return { ok: true, status: 'exists', list };

    if (list.length >= MAX_COMPARE_ITEMS) {
      window.dispatchEvent(
        new CustomEvent('compare:limit_reached', {
          detail: { max: MAX_COMPARE_ITEMS, list },
        })
      );
      return { ok: false, reason: 'limit_reached', list };
    }

    const nextList = setCompareList([...list, handle]);
    return { ok: true, status: 'added', list: nextList };
  }

  /**
   * @param {string} productHandle
   */
  function removeFromCompare(productHandle) {
    const handle = String(productHandle).trim();
    if (!handle) return { ok: false, reason: 'invalid_product_handle' };

    const list = getCompareList();
    if (!list.includes(handle)) return { ok: true, status: 'not_found', list };

    const nextList = setCompareList(list.filter((item) => item !== handle));
    return { ok: true, status: 'removed', list: nextList };
  }

  /**
   * @param {string} productHandle
   */
  function toggleCompare(productHandle) {
    const handle = String(productHandle).trim();
    if (!handle) return { ok: false, reason: 'invalid_product_handle' };

    return getCompareList().includes(handle) ? removeFromCompare(handle) : addToCompare(handle);
  }

  /**
   * @param {string[]=} listOverride
   */
  function updateCompareButtons(listOverride) {
    const compareList = Array.isArray(listOverride) ? listOverride : getCompareList();
    const atLimit = compareList.length >= MAX_COMPARE_ITEMS;
    const buttons = document.querySelectorAll('[data-compare-toggle]');

    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;

      const productHandle = String(button.dataset.productHandle || button.dataset.productId || '').trim();
      const inCompare = productHandle && compareList.includes(productHandle);
      const shouldDisableForLimit = atLimit && !inCompare;
      const buttonLimitText = button.getAttribute('data-compare-limit-message');

      if(button.classList.contains('text-button')) {
        const addLabel = button.dataset.compareAddLabel || 'Add to Compare';
        const removeLabel = button.dataset.compareRemoveLabel || 'Remove from Compare';
        button.querySelector('span').textContent = (inCompare ? removeLabel : addLabel);
      } else {
        const addLabel = button.dataset.compareAddLabel || 'Add to Compare';
        const removeLabel = button.dataset.compareRemoveLabel || 'Remove from Compare';
        button.setAttribute('title', inCompare ? removeLabel : addLabel);
      }

      button.dataset.compareState = inCompare ? 'added' : 'not-added';
      button.setAttribute('aria-pressed', String(Boolean(inCompare)));
      button.toggleAttribute('disabled', shouldDisableForLimit);
      if(shouldDisableForLimit){button.setAttribute('title', buttonLimitText);}
    });
  }

  /**
   * Check if current page is the compare page
   * @returns {boolean}
   */
  function isComparePage() {
    const currentPath = window.location.pathname + window.location.search;
    return currentPath.includes('/pages/compare');
  }

  /**
   * Create sticky notification element
   * @param {number} count
   * @returns {HTMLElement}
   */
  function createStickyNotification(count) {
    const notification = document.createElement('div');
    notification.className = 'compare-sticky-notification';
    
    notification.innerHTML = `
      <div class="compare-sticky-content">
        <div class="compare-sticky-info">
          <span class="compare-sticky-count">${count}</span>
          <span class="compare-sticky-text">item${count !== 1 ? 's' : ''} in compare</span>
        </div>
        <a href="/pages/compare" class="compare-sticky-btn">
          View Comparison
        </a>
      </div>
    `;

    // Apply styles
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 14px 16px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      min-width: 240px;
      animation: compareSlideIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
      font-family: inherit;
    `;

    return notification;
  }

  /**
   * Show sticky notification
   * @param {number} count
   */
  function showStickyNotification(count) {
    // Don't show on compare page
    if (isComparePage()) return;
    
    // Only show if more than one item
    if (count <= 1) return;

    // Remove existing notification
    const existing = document.querySelector('.compare-sticky-notification');
    if (existing) {
      existing.remove();
    }

    const notification = createStickyNotification(count);
    document.body.appendChild(notification);

    // Style the content
    const content = notification.querySelector('.compare-sticky-content');
    if (content) {
      content.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
      `;
    }

    const info = notification.querySelector('.compare-sticky-info');
    if (info) {
      info.style.cssText = `
        display: flex;
        align-items: center;
        gap: 4px;
      `;
    }

    const count_el = notification.querySelector('.compare-sticky-count');
    if (count_el) {
      count_el.style.cssText = `
        font-weight: 700;
        font-size: 16px;
        color: #000;
      `;
    }

    const text_el = notification.querySelector('.compare-sticky-text');
    if (text_el) {
      text_el.style.cssText = `
        font-size: 14px;
        color: #555;
        font-weight: 500;
      `;
    }

    const btn = notification.querySelector('.compare-sticky-btn');
    if (btn) {
      btn.style.cssText = `
        padding: 8px 14px;
        background: #000;
        color: #fff;
        border: none;
        border-radius: 25px;
        font-size: 13px;
        font-weight: 600;
        text-decoration: none;
        cursor: pointer;
        white-space: nowrap;
        transition: background 0.3s ease;
      `;
      
      btn.addEventListener('mouseenter', () => {
        btn.style.background = '#333';
      });
      
      btn.addEventListener('mouseleave', () => {
        btn.style.background = '#000';
      });
    }

    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (notification.parentElement) {
        notification.style.animation = 'compareSlideOut 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards';
        setTimeout(() => {
          if (notification.parentElement) {
            notification.remove();
          }
        }, 500);
      }
    }, 5000);
  }

  function onCompareToggleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-compare-toggle]');
    if (!(button instanceof HTMLElement)) return;

    event.preventDefault();

    const productHandle = String(button.dataset.productHandle || button.dataset.productId || '').trim();
    if (!productHandle) return;

    const result = toggleCompare(productHandle);
    if (!result.ok && result.reason === 'limit_reached') {
      const message = button.dataset.compareLimitMessage || `You can compare up to ${MAX_COMPARE_ITEMS} products.`;
      window.alert(message);
    }
  }

  function initCompare() {
    // Inject animations CSS
    const styleTag = document.createElement('style');
    styleTag.textContent = `
      @keyframes compareSlideIn {
        from {
          opacity: 0;
          transform: translate(20px, 20px);
        }
        to {
          opacity: 1;
          transform: translate(0, 0);
        }
      }

      @keyframes compareSlideOut {
        from {
          opacity: 1;
          transform: translate(0, 0);
        }
        to {
          opacity: 0;
          transform: translate(20px, 20px);
        }
      }

      @media (max-width: 640px) {
        .compare-sticky-notification {
          bottom: 10px !important;
          left: 10px !important;
          right: 10px !important;
          min-width: auto !important;
        }

        .compare-sticky-content {
          flex-direction: column !important;
          gap: 10px !important;
        }

        .compare-sticky-btn {
          width: 100% !important;
          text-align: center !important;
        }
      }
    `;
    document.head.appendChild(styleTag);

    updateCompareButtons();
    document.addEventListener('click', onCompareToggleClick);
    document.addEventListener('shopify:section:load', () => updateCompareButtons());
    window.addEventListener('pageshow', () => updateCompareButtons());
    
    // Listen for compare:updated event
    window.addEventListener('compare:updated', (event) => {
      showStickyNotification(event.detail.count);
    });
  }

  const Compare = {
    STORAGE_KEY,
    MAX_COMPARE_ITEMS,
    getCompareList,
    setCompareList,
    addToCompare,
    removeFromCompare,
    toggleCompare,
    updateCompareButtons,
    init: initCompare,
  };

  window.Compare = Compare;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCompare, { once: true });
  } else {
    initCompare();
  }